import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { getDayEndChecklist, checkoffDayEndItem } from '../services/api';
import { toLocalISODate } from '../utils/dateUtils';

const SECTION_LINKS = {
  wash: { to: '/wash', label: 'Vehicle Wash' },
  fuel: { to: '/fuel-fill', label: 'Fuel Fill' },
  trips: { to: '/trip-in', label: 'Vehicle In' },
  oil: { to: '/oil-change', label: 'Oil Change' },
};

const StatusBadge = ({ status }) => {
  if (status === 'auto_fulfilled') {
    return <span className="badge ok">Auto done</span>;
  }
  if (status === 'checked') {
    return <span className="badge ok">Checked</span>;
  }
  return <span className="badge warn">Open</span>;
};

function formatWhen(value) {
  if (!value) return '';
  try {
    return format(new Date(value), 'dd MMM yyyy HH:mm');
  } catch (_err) {
    return '';
  }
}

export default function DayEnd() {
  const [date, setDate] = useState(() => toLocalISODate());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkingId, setCheckingId] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [expandedNotes, setExpandedNotes] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const checklist = await getDayEndChecklist(date);
      setData(checklist);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load checklist');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCheckoff = async (item) => {
    setCheckingId(item.itemKey);
    try {
      const updated = await checkoffDayEndItem({
        date,
        category: item.category,
        refId: item.refId,
        notes: noteDrafts[item.itemKey] || '',
      });
      setData(updated);
      toast.success(`${item.vehicleName} marked done`);
      setExpandedNotes((prev) => ({ ...prev, [item.itemKey]: false }));
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save checkoff');
    } finally {
      setCheckingId(null);
    }
  };

  const summary = data?.summary || { total: 0, fulfilled: 0, open: 0 };
  const allDone = summary.total > 0 && summary.open === 0;

  return (
    <>
      <div className="page-header">
        <h2>Day End Checklist</h2>
        <p>
          End-of-day review for Zaman — confirm wash, fuel, returns, and oil change tasks for the selected date.
        </p>
      </div>

      <div className="card" style={{ marginBottom: 24, maxWidth: 420 }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Checklist date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
      </div>

      {loading ? (
        <p className="text-muted">Loading checklist…</p>
      ) : error ? (
        <div className="card">
          <div className="empty-state">
            <div className="icon">⚠</div>
            <p>{error}</p>
          </div>
        </div>
      ) : (
        <>
          <div
            className={`alert-banner ${allDone ? 'ok' : summary.open > 0 ? 'suspicious' : ''}`}
            style={{ marginBottom: 24 }}
          >
            <span>{allDone ? '✓' : '☑'}</span>
            <span>
              {summary.total === 0 ? (
                'No day-end tasks for this date — all clear.'
              ) : (
                <>
                  <strong>{summary.fulfilled} of {summary.total}</strong> items complete
                  {summary.open > 0 ? ` — ${summary.open} still open` : ' — all done'}
                </>
              )}
            </span>
          </div>

          {(data?.sections || []).map((section) => {
            const link = SECTION_LINKS[section.key];
            const openCount = (section.items || []).filter((i) => i.status === 'open').length;

            return (
              <div key={section.key} className="card" style={{ marginBottom: 20 }}>
                <div className="flex-between mb16">
                  <div>
                    <div className="card-title" style={{ marginBottom: 4 }}>{section.label}</div>
                    <p className="text-muted text-sm" style={{ margin: 0 }}>
                      {(section.items || []).length} item{(section.items || []).length === 1 ? '' : 's'}
                      {openCount > 0 ? ` · ${openCount} open` : ''}
                    </p>
                  </div>
                  {link && (
                    <Link to={link.to} className="btn btn-sm">
                      → {link.label}
                    </Link>
                  )}
                </div>

                {(section.items || []).length === 0 ? (
                  <div className="empty-state" style={{ padding: '16px 0' }}>
                    <p className="text-muted text-sm" style={{ margin: 0 }}>
                      {section.key === 'wash' && 'No wash planned for this date.'}
                      {section.key === 'fuel' && 'No vehicles need fuel refill right now.'}
                      {section.key === 'trips' && 'No vehicles still out.'}
                      {section.key === 'oil' && 'No oil changes due right now.'}
                    </p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {(section.items || []).map((item) => (
                      <div
                        key={item.itemKey}
                        style={{
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius)',
                          padding: '12px 14px',
                          borderLeft: `4px solid ${
                            item.status === 'open' ? 'var(--warn)' : 'var(--ok)'
                          }`,
                        }}
                      >
                        <div className="flex-between" style={{ alignItems: 'flex-start', gap: 12 }}>
                          <div>
                            <strong>{item.vehicleName}</strong>
                            {item.plate && (
                              <span className="mono text-muted text-sm" style={{ marginLeft: 8 }}>
                                {item.plate}
                              </span>
                            )}
                            <StatusBadge status={item.status} />
                            <div className="text-muted text-sm" style={{ marginTop: 6 }}>
                              {item.detail}
                            </div>
                            {item.fulfilledAt && item.status !== 'open' && (
                              <div className="text-muted text-sm" style={{ marginTop: 4 }}>
                                {item.source === 'manual' ? 'Checked' : 'Completed'} {formatWhen(item.fulfilledAt)}
                                {item.fulfilledBy && item.fulfilledBy !== 'system' ? ` · ${item.fulfilledBy}` : ''}
                              </div>
                            )}
                            {item.notes && (
                              <div className="text-sm" style={{ marginTop: 6 }}>
                                Note: {item.notes}
                              </div>
                            )}
                          </div>

                          {item.status === 'open' && (
                            <div style={{ minWidth: 140, textAlign: 'right' }}>
                              {expandedNotes[item.itemKey] && (
                                <textarea
                                  rows={2}
                                  placeholder="Optional note…"
                                  value={noteDrafts[item.itemKey] || ''}
                                  onChange={(e) =>
                                    setNoteDrafts((prev) => ({ ...prev, [item.itemKey]: e.target.value }))
                                  }
                                  style={{ marginBottom: 8, width: '100%' }}
                                />
                              )}
                              <button
                                type="button"
                                className="btn btn-sm"
                                style={{ marginRight: 6 }}
                                onClick={() =>
                                  setExpandedNotes((prev) => ({
                                    ...prev,
                                    [item.itemKey]: !prev[item.itemKey],
                                  }))
                                }
                              >
                                {expandedNotes[item.itemKey] ? 'Hide note' : 'Add note'}
                              </button>
                              <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                disabled={checkingId === item.itemKey}
                                onClick={() => handleCheckoff(item)}
                              >
                                {checkingId === item.itemKey ? '…' : '✓ Mark done'}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </>
  );
}
